import { useBalanceChecker } from "app/components/common";
import { COPY } from "app/constants";
import {
    CapType,
    LendingVaultExpanded,
    summarizeVaultSuppliedDeposits,
    useActiveWallet,
    useVaultMaxAmountFromCap,
    useWalletBalanceByMint
} from "@bridgesplit/abf-react";
import { useTransactionData } from "app/components/transactions";
import { TransactionTypes } from "app/types";

import { useVaultDepositContext } from "./VaultDepositContext";
import { useVaultContext } from "../VaultContext";

export function useVaultDepositBalance() {
    const { form } = useVaultDepositContext();
    const { vaultExpanded } = useVaultContext();

    const balances = useMaxDepositBalance();

    return useBalanceChecker({
        amount: form.amount,
        customBalance: balances?.maxDeposit,
        skip: !balances?.maxDeposit,
        mint: vaultExpanded?.principalMetadata?.mint
    });
}

export function useMaxDepositBalance() {
    const { vaultExpanded } = useVaultContext();
    const summary = useVaultMaxAmountFromCap(vaultExpanded, CapType.Supply);
    const { getTransactionFees } = useTransactionData();

    const solBuffer = getTransactionFees(TransactionTypes.VaultDeposit).totalFeeAmount;

    const { activeWallet } = useActiveWallet(); //Required to lift the balance check to the top level
    const walletBalance = useWalletBalanceByMint(activeWallet, vaultExpanded?.principalMetadata?.mint, {
        skip: !activeWallet,
        fullSolBalance: false,
        solBuffer
    });

    if (!summary || !walletBalance.data?.amount) return undefined;

    const maxDeposit = Math.min(
        summary?.maxBalanceRemaining?.toNumber() ?? Number.MAX_SAFE_INTEGER,
        walletBalance.data?.amount
    );

    return {
        maxDeposit
    };
}

export function useDepositUpdateAmount() {
    const { setForm } = useVaultDepositContext();
    return (amount: number | undefined) => setForm((prev) => ({ ...prev, amount }));
}

export function useVaultDepositCtaText(vaultExpanded: LendingVaultExpanded | undefined) {
    const { supplyLimitReached } = summarizeVaultSuppliedDeposits(vaultExpanded);

    if (supplyLimitReached) {
        return COPY.SUPPLY_LIMIT_REACHED;
    }

    return COPY.VAULT_DEPOSIT_CTA;
}
